import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { graphql } from '@lib/api';
import GQL from '@lib/cms/gql';
import { gql, useQuery } from '@apollo/client';
import { toKebabCase, getSlug } from '@lib/utils';

import Container from '@layout/container';
import Jumbotron from '@components/jumbotron';
import Layout from '@layout/index';
import Grid from '@layout/grid';
import Image from '@components/image';
import Card from '@components/card';
import Pagination from '@components/pagination';
import SortButton from '@components/sort-button';

import { fixtures } from '@content/results';

import {
  OFFICE_LISTINGS_QUERY,
  OFFICE_PATHS_QUERY,
  OFFICE_DETAIL_QUERY,
  OFFICE_ATTRIBUTES
} from '@lib/fragments';

import { copyright_section, listings_header } from '../../styles.module.scss';

export async function getStaticPaths() {
  const { data } = await GQL.fetch(OFFICE_PATHS_QUERY);

  const paths = data?.officeCollection?.items.map((item) => {
    const kebabSlug = toKebabCase(item.slug);
    return { params: { slug: kebabSlug } };
  });

  return {
    fallback: false,
    paths
  };
}

export async function getStaticProps(context) {
  const slug = context.params.slug;
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment || null;
  const { data: cmsFetchData } = await GQL.fetch(
    OFFICE_DETAIL_QUERY,
    { preview, slug },
    preview,
    previewEnv
  );
  const { officeId } = cmsFetchData.officeCollection.items[0];

  const data = await graphql(
    gql`
      ${OFFICE_ATTRIBUTES}
      query GetOfficeDetails($officeId: Int!) {
        officeSearch(where: { officeId: { eq: $officeId } }) {
          __typename
          items {
            ...Office
          }
        }
      }
    `,
    { officeId }
  );

  const officeData = data?.officeSearch?.items[0];
  const cmsData = cmsFetchData?.officeCollection?.items[0];

  if (!cmsData || !officeData) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      cmsData,
      officeId,
      officeData,
      preview
    },
    revalidate: 300
  };
}

// ---------------------------------------------------------

const PAGESIZE = 15;

// ---------------------------------------------------------

const OfficeListingDetailPage = ({ officeData, officeId, cmsData, preview }) => {
  const router = useRouter();
  const [sortBy, setSortBy] = useState({
    label: 'Newest Listings',
    value: [{ daysSinceNew: 'ASC' }, { price: 'DESC' }, { address: 'ASC' }]
  });
  const [copyrightData, setCopyrightData] = useState([]);
  const [total, setTotal] = useState();
  const topRef = useRef();

  useEffect(() => {
    if (!router.isReady) return;

    if (router?.query?.sortby) {
      const sortItem = fixtures.sortButtonOptions.find((item) => {
        return item.label === router.query.sortby;
      });
      setSortBy(sortItem);
    }
  }, [router?.isReady, router?.query]);

  const handleSortClick = (el) => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          page: 1,
          sortby: el.label
        }
      },
      undefined,
      { shallow: true }
    );
  };

  const { loading, error, data } = useQuery(OFFICE_LISTINGS_QUERY, {
    skip: !router.isReady === undefined,
    variables: {
      officeId: officeId,
      skip: (router.query.page - 1 || 0) * PAGESIZE,
      sortBy: sortBy.value,
      take: PAGESIZE
    }
  });

  if (total === undefined && data?.listingSearch?.totalCount > 0) {
    setTotal(data?.listingSearch?.totalCount ?? 0);
  }

  useEffect(() => {
    const AllCopyrightData = (data?.listingSearch?.items || []).reduce((acc, { mlsCopyright }) => {
      return acc.find((item) => item.description === mlsCopyright?.description)
        ? acc
        : [...acc, mlsCopyright];
    }, []);

    setCopyrightData(AllCopyrightData);
  }, []);

  return (
    <Layout
      meta={{
        description: cmsData?.metaDescription || cmsData?.description,
        image: cmsData?.metaImage || { url: officeData?.photoUrl },
        title: cmsData?.title || officeData?.name
      }}
      preview={preview}
    >
      <Jumbotron
        theme="bar"
        image={{
          alt: cmsData?.headerImage?.alt,
          height: cmsData?.headerImage?.height,
          src: cmsData?.headerImage?.url || officeData.photoUrl,
          width: cmsData?.headerImage?.width
        }}
        ctas={[
          officeData.address && {
            label: `${officeData.address} ${officeData.city}, ${officeData.state}`,
            theme: 'text-only-white',
            url: officeData.geoLocationMapExternalLink
          },
          officeData?.phone && {
            label: officeData?.phone,
            theme: 'text-only-white',
            url: `tel:${officeData?.phone}`
          },
          {
            label: 'Back to office page',
            theme: 'text-only-white-with-caret',
            url: `/office/${cmsData.slug}`
          }
        ].filter((item) => item)}
        body="Office"
        title={cmsData.title || officeData.name}
      />

      <Container gridOptions={{ layout: 'single' }} spacing="small">
        <div ref={topRef} className={listings_header}>
          <div>
            {error ? (
              <p className="error-message">{error.message}</p>
            ) : loading ? (
              <p>Loading...</p>
            ) : (
              <p>{total || 0} properties</p>
            )}
          </div>

          <div>
            <SortButton
              selection={sortBy}
              options={fixtures.sortButtonOptions}
              onChange={handleSortClick}
            />
          </div>
        </div>

        <Grid layout="third">
          {data?.listingSearch?.items &&
            data?.listingSearch?.items.map((listing, index) => {
              return (
                <Card
                  alertPills={listing.alertPills}
                  image={{
                    src: !listing.mainPhoto
                      ? 'https://place-hold.it/340x231/green/white&text=FPO&fontsize=16'
                      : `${listing.mainPhoto.midSizeImageUrl}`
                  }}
                  property={{
                    ...listing,
                    details: [
                      { br: listing.bedroomCount },
                      {
                        ba: listing.partBathroomCount
                          ? `${listing.fullBathroomCount}/${listing.partBathroomCount}`
                          : listing.fullBathroomCount
                      },
                      { ac: listing.acres }
                    ],
                    price: listing.priceFormatted,
                    realtor: {
                      isActiveSibcyAgent: true,
                      primaryRealtorName: listing?.contact?.primaryContactName,
                      secondaryRealtorName: listing?.contact?.secondaryContactName
                    }
                  }}
                  url={
                    listing.canonicalUrl
                      ? getSlug(listing.canonicalUrl)
                      : `/listing/${listing.listingId}`
                  }
                  key={index}
                />
              );
            })}
        </Grid>
      </Container>

      {total > PAGESIZE && (
        <Container spacing="medium" textAlignment="center" gridOptions={{ layout: 'single' }}>
          <Pagination
            items={Array.from(Array(total || 0).keys())}
            per={PAGESIZE}
            scrollToPosition={topRef}
          />
        </Container>
      )}

      <Container textAlignment="center" gridOptions={{ layout: 'single' }}>
        {copyrightData &&
          copyrightData.map((el, i) => {
            return (
              <section className={copyright_section} key={i}>
                <div>
                  <Image
                    layout="fill"
                    objectFit="contain"
                    image={{
                      height: '40px',
                      src: el.logoUrl,
                      width: '96px'
                    }}
                  />
                </div>
                <div>{el.description}</div>
              </section>
            );
          })}
      </Container>
    </Layout>
  );
};

export default OfficeListingDetailPage;
